import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { Fragment } from 'preact';
import { admin as adminApi } from '../lib/api';
import type { AdminStats, AdminMessage, AdminUser } from '../lib/types';

interface AdminProps {
  domain: string;
}

export function Admin({ domain }: AdminProps) {
  const [secret, setSecret] = useState('');
  const [connected, setConnected] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [tab, setTab] = useState<'messages' | 'users' | 'quarantine' | 'audit'>('messages');

  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [quarantine, setQuarantine] = useState<any[]>([]);
  const [auditEntries, setAuditEntries] = useState<any[]>([]);
  const [expandedAudit, setExpandedAudit] = useState<Set<string>>(new Set());
  const [page, setPage] = useState({ messages: 0, users: 0 });
  const limit = 20;
  const secretRef = useRef('');

  const showToast = useCallback((msg: string, type: string) => {
    const el = document.createElement('div');
    el.className = 'toast ' + type;
    el.textContent = msg;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 4000);
  }, []);

  const adminHeaders = useCallback(
    () => ({ 'x-admin-secret': secretRef.current, 'content-type': 'application/json' }),
    [],
  );

  const formatBytes = (bytes: number): string => {
    if (!bytes || bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + units[i];
  };

  const formatRelativeTime = (ts: number | null): string => {
    if (!ts) return '—';
    const diff = Date.now() - ts;
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
    if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
    return Math.floor(diff / 86400000) + 'd ago';
  };

  const truncate = (str: string, n: number): string => {
    if (!str) return '';
    return str.length > n ? str.substring(0, n) + '...' : str;
  };

  const loadMessages = useCallback(async () => {
    try {
      const data = await adminApi.messages(secretRef.current, limit, page.messages * limit);
      setMessages(data.messages || []);
      setMessagesTotal(data.total);
    } catch {
      showToast('Failed to load messages', 'error');
    }
  }, [page.messages, showToast]);

  const loadUsers = useCallback(async () => {
    try {
      const data = await adminApi.users(secretRef.current, limit, page.users * limit);
      setUsers(data.users || []);
      setUsersTotal(data.total);
    } catch {
      showToast('Failed to load users', 'error');
    }
  }, [page.users, showToast]);

  const loadQuarantine = useCallback(async () => {
    try {
      const data = await adminApi.quarantine(secretRef.current);
      setQuarantine(data.messages || []);
    } catch {
      showToast('Failed to load quarantine', 'error');
    }
  }, [showToast]);

  const loadAuditLog = useCallback(async () => {
    try {
      const data = await adminApi.auditLog(secretRef.current);
      setAuditEntries(data.entries || []);
    } catch {
      showToast('Failed to load audit log', 'error');
    }
  }, [showToast]);

  const handleDeleteQuarantine = useCallback(
    async (id: string) => {
      if (!confirm('Delete this quarantined message? This cannot be undone.')) return;
      try {
        await adminApi.deleteQuarantine(secretRef.current, id);
        showToast('Quarantined message deleted', 'success');
        await loadQuarantine();
      } catch {
        showToast('Error deleting quarantined message', 'error');
      }
    },
    [loadQuarantine, showToast],
  );

  const handleReleaseQuarantine = useCallback(
    async (id: string) => {
      try {
        await adminApi.releaseQuarantine(secretRef.current, id);
        showToast('Message released from quarantine', 'success');
        await loadQuarantine();
      } catch {
        showToast('Error releasing quarantined message', 'error');
      }
    },
    [loadQuarantine, showToast],
  );

  const toggleAuditRow = useCallback((id: string) => {
    setExpandedAudit((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const auditEventColor = (eventType: string): string => {
    if (!eventType) return 'var(--text-muted)';
    if (eventType === 'auth_login') return 'var(--success)';
    if (eventType === 'email_sent') return '#58a6ff';
    if (eventType === 'alias_claimed') return '#d29922';
    if (eventType.startsWith('admin_')) return 'var(--danger)';
    return 'var(--text-muted)';
  };

  const handleTabSwitch = useCallback(
    (next: 'messages' | 'users' | 'quarantine' | 'audit') => {
      setTab(next);
      if (next === 'quarantine' && quarantine.length === 0) void loadQuarantine();
      if (next === 'audit' && auditEntries.length === 0) void loadAuditLog();
    },
    [quarantine.length, auditEntries.length, loadQuarantine, loadAuditLog],
  );

  const refreshStats = useCallback(async () => {
    if (!secretRef.current) return;
    try {
      const data = await adminApi.stats(secretRef.current);
      setStats(data);
    } catch {
      // silent
    }
  }, []);

  const handleConnect = useCallback(async () => {
    if (!secret) return;
    setAuthError(false);
    secretRef.current = secret;
    try {
      const data = await adminApi.stats(secret);
      setStats(data);
      setConnected(true);
      await loadMessages();
      await loadUsers();
      showToast('Connected to admin panel', 'success');
    } catch {
      setAuthError(true);
      showToast('Invalid admin secret', 'error');
      secretRef.current = '';
    }
  }, [secret, loadMessages, loadUsers, showToast]);

  const handleDisconnect = useCallback(() => {
    secretRef.current = '';
    setConnected(false);
    setSecret('');
    setStats(null);
    showToast('Disconnected', '');
  }, [showToast]);

  const handleDeleteMessage = useCallback(
    async (id: string) => {
      if (!confirm('Delete this message? This cannot be undone.')) return;
      try {
        const data = await adminApi.deleteMessage(secretRef.current, id);
        showToast(
          'Deleted message (' + data.r2ObjectsRemoved + ' R2 objects removed)',
          'success',
        );
        await loadMessages();
        await refreshStats();
      } catch {
        showToast('Error deleting message', 'error');
      }
    },
    [loadMessages, refreshStats, showToast],
  );

  useEffect(() => {
    if (connected) {
      const interval = setInterval(() => void refreshStats(), 60000);
      return () => clearInterval(interval);
    }
  }, [connected, refreshStats]);

  useEffect(() => {
    if (connected) void loadMessages();
  }, [page.messages, connected, loadMessages]);

  useEffect(() => {
    if (connected) void loadUsers();
  }, [page.users, connected, loadUsers]);

  const msgStart = messagesTotal === 0 ? 0 : page.messages * limit + 1;
  const msgEnd = Math.min(page.messages * limit + limit, messagesTotal);
  const userStart = usersTotal === 0 ? 0 : page.users * limit + 1;
  const userEnd = Math.min(page.users * limit + limit, usersTotal);

  return (
    <>
      <header>
        <h1>
          <span>{domain}</span> Admin
        </h1>
        {connected ? (
          <div id="header-connected" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style="font-size:12px;color:var(--success);">&#9679; Connected</span>
            <button class="btn btn-ghost btn-sm" onClick={handleDisconnect}>
              Disconnect
            </button>
          </div>
        ) : null}
        <div class="spacer" />
      </header>

      {!connected ? (
        <div class="auth-overlay">
          <div class="auth-card">
            <h2>Admin Dashboard</h2>
            <p>Enter the admin secret to access the {domain} admin panel.</p>
            <div class="field">
              <input
                type="password"
                placeholder="Admin secret"
                autocomplete="off"
                value={secret}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleConnect();
                }}
                onInput={(e) => setSecret((e.target as HTMLInputElement).value)}
              />
            </div>
            {authError && (
              <div class="auth-error" style={{ display: 'block' }}>
                Invalid admin secret
              </div>
            )}
            <button class="btn" style="width:100%" onClick={() => void handleConnect()}>
              Connect
            </button>
          </div>
        </div>
      ) : (
        <div class="main active">
          {stats && (
            <>
              <div class="stats-row">
                <div class="stat-card">
                  <div class="label">Users</div>
                  <div class="value">{stats.users ?? 0}</div>
                </div>
                <div class="stat-card">
                  <div class="label">Messages</div>
                  <div class="value">{stats.messages ?? 0}</div>
                </div>
                <div class="stat-card">
                  <div class="label">Active Addresses</div>
                  <div class="value">{stats.activeAddresses ?? 0}</div>
                </div>
                <div class="stat-card">
                  <div class="label">Storage Used</div>
                  <div class="value">{formatBytes(stats.storageBytes)}</div>
                </div>
              </div>

              <div class="proofs-row">
                <div class="proof-card">
                  <div class="label">Cashu Total</div>
                  <div class="value">{stats.cashuProofs?.total ?? 0}</div>
                </div>
                <div class="proof-card held">
                  <div class="label">Held</div>
                  <div class="value">{stats.cashuProofs?.held ?? 0}</div>
                </div>
                <div class="proof-card redeemed">
                  <div class="label">Redeemed</div>
                  <div class="value">{stats.cashuProofs?.redeemed ?? 0}</div>
                </div>
                <div class="proof-card failed">
                  <div class="label">Failed</div>
                  <div class="value">{stats.cashuProofs?.failed ?? 0}</div>
                </div>
              </div>
            </>
          )}

          <div class="tabs">
            <button
              class={'tab' + (tab === 'messages' ? ' active' : '')}
              onClick={() => handleTabSwitch('messages')}
            >
              Messages
            </button>
            <button
              class={'tab' + (tab === 'users' ? ' active' : '')}
              onClick={() => handleTabSwitch('users')}
            >
              Users
            </button>
            <button
              class={'tab' + (tab === 'quarantine' ? ' active' : '')}
              onClick={() => handleTabSwitch('quarantine')}
            >
              Quarantine
            </button>
            <button
              class={'tab' + (tab === 'audit' ? ' active' : '')}
              onClick={() => handleTabSwitch('audit')}
            >
              Audit Log
            </button>
          </div>

          {tab === 'messages' && (
            <div class="section active">
              <div class="table-wrap">
                {messages.length === 0 ? (
                  <div class="empty">No messages found</div>
                ) : (
                  <>
                    <table>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>From</th>
                          <th>To</th>
                          <th>Subject</th>
                          <th>Status</th>
                          <th>Received</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {messages.map((m) => (
                          <tr key={m.id}>
                            <td class="mono truncate" title={m.id}>
                              {truncate(m.id, 16)}
                            </td>
                            <td>{m.fromAddr || '—'}</td>
                            <td>{m.toAddr || '—'}</td>
                            <td class="truncate" title={m.subject || ''}>
                              {truncate(m.subject || '(no subject)', 40)}
                            </td>
                            <td>
                              <span class={'badge ' + (m.ingestStatus || 'pending')}>
                                {m.ingestStatus || 'pending'}
                              </span>
                            </td>
                            <td>{formatRelativeTime(m.receivedAt)}</td>
                            <td>
                              <button
                                class="btn btn-danger btn-sm"
                                onClick={() => void handleDeleteMessage(m.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div class="pagination">
                      <div class="pagination-info">
                        {msgStart}–{msgEnd} of {messagesTotal}
                      </div>
                      <div class="pagination-btns">
                        <button
                          class="btn btn-ghost btn-sm"
                          disabled={page.messages === 0}
                          onClick={() => setPage((p) => ({ ...p, messages: Math.max(0, p.messages - 1) }))}
                        >
                          Previous
                        </button>
                        <button
                          class="btn btn-ghost btn-sm"
                          disabled={msgEnd >= messagesTotal}
                          onClick={() => setPage((p) => ({ ...p, messages: p.messages + 1 }))}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {tab === 'users' && (
            <div class="section active">
              <div class="table-wrap">
                {users.length === 0 ? (
                  <div class="empty">No users found</div>
                ) : (
                  <>
                    <table>
                      <thead>
                        <tr>
                          <th>Pubkey</th>
                          <th>Display Name</th>
                          <th>Addresses</th>
                          <th>Messages</th>
                          <th>Last Login</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.pubkey}>
                            <td class="mono truncate" title={u.pubkey}>
                              {truncate(u.pubkey, 20)}
                            </td>
                            <td>{u.displayName || '—'}</td>
                            <td>{u.addressCount ?? 0}</td>
                            <td>{u.messageCount ?? 0}</td>
                            <td>{formatRelativeTime(u.lastLoginAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div class="pagination">
                      <div class="pagination-info">
                        {userStart}–{userEnd} of {usersTotal}
                      </div>
                      <div class="pagination-btns">
                        <button
                          class="btn btn-ghost btn-sm"
                          disabled={page.users === 0}
                          onClick={() => setPage((p) => ({ ...p, users: Math.max(0, p.users - 1) }))}
                        >
                          Previous
                        </button>
                        <button
                          class="btn btn-ghost btn-sm"
                          disabled={userEnd >= usersTotal}
                          onClick={() => setPage((p) => ({ ...p, users: p.users + 1 }))}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {tab === 'quarantine' && (
            <div class="section active">
              <div class="table-wrap">
                {quarantine.length === 0 ? (
                  <div class="empty">No quarantined messages</div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>From</th>
                        <th>To</th>
                        <th>Reason</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quarantine.map((q) => {
                        const qId = q.id ?? q.message_id ?? '';
                        return (
                          <tr key={qId}>
                            <td>{q.from_addr || q.from || '—'}</td>
                            <td>{q.to_addr || q.to || '—'}</td>
                            <td class="truncate" title={q.reason || ''}>
                              {truncate(q.reason || '—', 40)}
                            </td>
                            <td>{formatRelativeTime(q.created_at ?? q.quarantined_at ?? q.received_at)}</td>
                            <td style={{ display: 'flex', gap: '6px' }}>
                              <button
                                class="btn btn-danger btn-sm"
                                onClick={() => void handleDeleteQuarantine(qId)}
                              >
                                Delete
                              </button>
                              <button
                                class="btn btn-sm"
                                style={{
                                  background: 'var(--success)',
                                  color: '#fff',
                                  border: 'none',
                                  cursor: 'pointer',
                                }}
                                onClick={() => void handleReleaseQuarantine(qId)}
                              >
                                Release
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {tab === 'audit' && (
            <div class="section active">
              <div class="table-wrap">
                {auditEntries.length === 0 ? (
                  <div class="empty">No audit entries</div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Event Type</th>
                        <th>Pubkey</th>
                        <th>Timestamp</th>
                        <th>Metadata</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditEntries.map((a, i) => {
                        const rowId = a.id != null ? String(a.id) : `row-${i}`;
                        const expanded = expandedAudit.has(rowId);
                        const eventType = a.event_type || a.eventType || '';
                        return (
                          <Fragment key={rowId}>
                            <tr
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleAuditRow(rowId)}
                            >
                              <td>
                                <span
                                  style={{
                                    color: auditEventColor(eventType),
                                    fontWeight: 600,
                                    fontFamily: 'var(--mono-font, monospace)',
                                  }}
                                >
                                  {eventType || '—'}
                                </span>
                              </td>
                              <td class="mono truncate" title={a.pubkey || ''}>
                                {truncate(a.pubkey || '—', 20)}
                              </td>
                              <td>{formatRelativeTime(a.created_at ?? a.timestamp)}</td>
                              <td>
                                {a.metadata || a.details ? (
                                  <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                                    {expanded ? '▲ Hide' : '▼ View'}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--text-muted)' }}>—</span>
                                )}
                              </td>
                            </tr>
                            {expanded && (a.metadata || a.details) && (
                              <tr key={rowId + '-meta'}>
                                <td colSpan={4} style={{ background: 'rgba(255,255,255,0.02)' }}>
                                  <pre
                                    style={{
                                      margin: '0',
                                      padding: '8px',
                                      fontSize: '12px',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      color: 'var(--text-muted)',
                                    }}
                                  >
                                    {(() => {
                                      try {
                                        return JSON.stringify(a.metadata ?? a.details, null, 2);
                                      } catch {
                                        return String(a.metadata ?? a.details);
                                      }
                                    })()}
                                  </pre>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
